import React, { useState, useEffect } from "react";
import {
  Tag,
  Printer,
  Wifi,
  WifiOff,
  Check,
  CheckSquare,
  Square,
  Filter,
  Usb,
  Unplug,
  Download,
  HelpCircle,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { getReceiptConfig, setReceiptConfig, THERMAL_WIDTHS } from "../utils/receiptConfig";
import { executePrintLabel } from "../utils/printManager";
import { masterService } from "../api/masterService";
import { useNotification } from "../contexts/NotificationContext";
import {
  isWebUSBSupported,
  requestUsbPrinter,
  getSavedUsbPrinter,
  disconnectUsbPrinter,
} from "../utils/usbPrintManager";
import {
  isWebSerialSupported,
  requestSerialPrinter,
  getSavedSerialPrinter,
  disconnectSerialPrinter,
} from "../utils/serialPrintManager";
import {
  getSavedWindowsPrinter,
  setSavedWindowsPrinter,
  disconnectWindowsPrinter,
  isWindowsPrintBridgeAvailable,
  listWindowsPrinters,
} from "../utils/windowsPrintBridge";

const HELPER_DOWNLOAD_URL = "https://drive.google.com/file/d/1DVEGq8lQZ8kTXHUEf2SW5WPPKj0oN0rz/view?usp=sharing";

/**
 * Komponen konfigurasi printer label pesanan.
 * Kuliner only — hanya tampil saat PREVIEW_APPS=true.
 */
export default function LabelPrinterConfig({ config, onChange }) {
  const { showSuccess, showError } = useNotification();
  // Bluetooth Android
  const [isScanning, setIsScanning] = useState(false);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [showDeviceList, setShowDeviceList] = useState(false);

  // USB WebUSB
  const [usbPrinter, setUsbPrinter] = useState(() => getSavedUsbPrinter());
  const [usbBusy, setUsbBusy] = useState(false);
  const [usbMessage, setUsbMessage] = useState("");

  // Serial COM
  const [serialPrinter, setSerialPrinter] = useState(() => getSavedSerialPrinter());
  const [serialBusy, setSerialBusy] = useState(false);
  const [serialMessage, setSerialMessage] = useState("");

  // Windows Spooler
  const [windowsPrinter, setWindowsPrinter] = useState(() =>
    config.labelPrinterName ? { name: config.labelPrinterName } : null,
  );
  const [windowsPrinters, setWindowsPrinters] = useState([]);
  const [windowsBusy, setWindowsBusy] = useState(false);
  const [windowsMessage, setWindowsMessage] = useState("");

  const [testPrinting, setTestPrinting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  useEffect(() => {
    setWindowsPrinter(
      config.labelPrinterName ? { name: config.labelPrinterName } : null,
    );
  }, [config.labelPrinterName]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoadingCategories(true);
      const res = await masterService.categories.list({ page: 1, limit: 100 });
      let list = [];
      if (res.data?.data && Array.isArray(res.data.data)) {
        list = res.data.data;
      } else if (res.data && Array.isArray(res.data)) {
        list = res.data;
      }
      setCategories(list);
    } catch (error) {
      console.error("Gagal mengambil kategori untuk label:", error);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const updateConfig = (patch) => {
    onChange(patch);
    const current = getReceiptConfig();
    setReceiptConfig({ ...current, ...patch });
  };

  const handleToggleLabel = () => updateConfig({ orderLabelEnabled: !config.orderLabelEnabled });
  const handleToggleAutoLabel = () => updateConfig({ autoPrintLabelEnabled: !config.autoPrintLabelEnabled });
  const handleWidthChange = (w) => updateConfig({ labelPrinterWidth: w });

  // Mapping Kategori Label
  const isAllCategories = !config.labelCategories || config.labelCategories === "ALL";
  const selectedCategoryIds = Array.isArray(config.labelCategories) ? config.labelCategories : [];

  const handleSetAllCategories = () => {
    updateConfig({
      labelCategories: "ALL",
      labelCategoryNames: [],
    });
  };

  const handleSetCustomCategories = () => {
    if (isAllCategories) {
      const allIds = categories.map((c) => c.id);
      const allNames = categories.map((c) => c.name);
      updateConfig({
        labelCategories: allIds,
        labelCategoryNames: allNames,
      });
    }
  };

  const handleToggleCategory = (id, name) => {
    let currentIds = isAllCategories ? categories.map((c) => c.id) : [...selectedCategoryIds];
    let nextIds = [];
    if (currentIds.includes(id)) {
      nextIds = currentIds.filter((cid) => cid !== id);
    } else {
      nextIds = [...currentIds, id];
    }
    const nextNames = nextIds
      .map((cid) => categories.find((c) => c.id === cid)?.name)
      .filter(Boolean);

    updateConfig({
      labelCategories: nextIds,
      labelCategoryNames: nextNames,
    });
  };

  const handleSelectAllCategories = () => {
    const allIds = categories.map((c) => c.id);
    const allNames = categories.map((c) => c.name);
    updateConfig({
      labelCategories: allIds,
      labelCategoryNames: allNames,
    });
  };

  const handleClearAllCategories = () => {
    updateConfig({
      labelCategories: [],
      labelCategoryNames: [],
    });
  };

  // USB Handlers
  const handleConnectUsb = async () => {
    setUsbBusy(true);
    setUsbMessage("");
    try {
      const saved = await requestUsbPrinter();
      disconnectWindowsPrinter();
      await disconnectSerialPrinter();
      setWindowsPrinter(null);
      setSerialPrinter(null);
      setUsbPrinter(saved);
      setUsbMessage("Printer USB siap. Cetak label akan memakai ESC POS langsung.");
    } catch (error) {
      setUsbMessage(error?.message || "Printer USB belum berhasil disambungkan.");
    } finally {
      setUsbBusy(false);
    }
  };

  const handleDisconnectUsb = async () => {
    await disconnectUsbPrinter();
    setUsbPrinter(null);
    setUsbMessage("Printer USB dilepas dari perangkat ini.");
  };

  // Serial COM Handlers
  const handleConnectSerial = async () => {
    setSerialBusy(true);
    setSerialMessage("");
    try {
      const saved = await requestSerialPrinter({ baudRate: 9600 });
      await disconnectUsbPrinter();
      disconnectWindowsPrinter();
      setUsbPrinter(null);
      setWindowsPrinter(null);
      setSerialPrinter(saved);
      setSerialMessage("Port COM siap. Cetak label akan memakai ESC POS langsung.");
    } catch (error) {
      setSerialMessage(error?.message || "Port COM belum berhasil disambungkan.");
    } finally {
      setSerialBusy(false);
    }
  };

  const handleDisconnectSerial = async () => {
    await disconnectSerialPrinter();
    setSerialPrinter(null);
    setSerialMessage("Port COM dilepas dari perangkat ini.");
  };

  // Windows Spooler Handlers
  const handleFindWindowsPrinters = async () => {
    setWindowsBusy(true);
    setWindowsMessage("");
    try {
      const available = await isWindowsPrintBridgeAvailable();
      if (!available) {
        throw new Error("Helper Windows belum berjalan. Jalankan printer bridge terlebih dahulu.");
      }
      const list = await listWindowsPrinters();
      setWindowsPrinters(list);
      if (list.length === 0) {
        setWindowsMessage("Tidak ada printer Windows yang terdeteksi.");
      }
    } catch (error) {
      setWindowsMessage(error?.message || "Gagal membaca daftar printer Windows.");
    } finally {
      setWindowsBusy(false);
    }
  };

  const handleSelectWindowsPrinter = (name) => {
    updateConfig({ labelPrinterName: name });
    setWindowsPrinter(name ? { name } : null);
    setWindowsMessage(
      name
        ? `Printer Label Windows dipilih: ${name}`
        : "Printer Label Windows dilepas.",
    );
  };

  const handleDisconnectWindowsPrinter = () => {
    updateConfig({ labelPrinterName: "" });
    setWindowsPrinter(null);
    setWindowsMessage("Printer Label Windows dilepas dari perangkat ini.");
  };

  // Bluetooth Android Handlers
  const handleScanBluetooth = () => {
    if (!window.bluetoothSerial) {
      showError("Bluetooth printer hanya tersedia di aplikasi Android.");
      return;
    }
    setIsScanning(true);
    setAvailableDevices([]);
    setShowDeviceList(false);
    window.bluetoothSerial.list(
      (devices) => { setIsScanning(false); setAvailableDevices(devices || []); setShowDeviceList(true); },
      () => { setIsScanning(false); showError("Gagal memindai bluetooth. Pastikan bluetooth aktif."); }
    );
  };

  const handleSelectDevice = (device) => {
    updateConfig({
      labelPrinterMac: device.address || device.id || "",
      labelPrinterName: device.name || device.address || "Printer Label",
    });
    setShowDeviceList(false);
  };

  const handleDisconnectBluetooth = () => updateConfig({ labelPrinterMac: "", labelPrinterName: "" });

  const handleTestPrint = async () => {
    if (testPrinting) return;
    setTestPrinting(true);
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const datetime = `${pad(now.getDate())}/${pad(now.getMonth()+1)}/${String(now.getFullYear()).slice(2)} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const sample = [
      {
        storeName: localStorage.getItem("tenantName") || "Nama Toko",
        customerName: "Pelanggan Contoh",
        salesTypeName: "Take Away",
        productName: "Matcha Latte Original",
        variantName: "",
        qty: 1, unit: "Cup",
        notes: "Less sugar",
        addons: [{ name: "Boba", qty: 1 }],
        datetime, index: 1, total: 2,
      },
      {
        storeName: localStorage.getItem("tenantName") || "Nama Toko",
        customerName: "Pelanggan Contoh",
        salesTypeName: "Take Away",
        productName: "Caramel Macchiato",
        variantName: "Iced",
        qty: 1, unit: "Cup",
        notes: "",
        addons: [],
        datetime, index: 2, total: 2,
      },
    ];
    try {
      const res = await executePrintLabel(sample);
      if (res?.method === "bluetooth") {
        showSuccess("Sampel label berhasil dikirim ke printer Bluetooth");
      } else {
        showSuccess("Sampel label berhasil dicetak ke printer");
      }
    } catch (e) {
      showError(e?.message || "Printer label belum terhubung. Silakan periksa Pengaturan Printer.");
    } finally {
      setTestPrinting(false);
    }
  };

  const isBluetoothConnected = !!config.labelPrinterMac;

  return (
    <div className="mt-6 bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-teal-50 ring-1 ring-teal-200/70 flex items-center justify-center shrink-0">
            <Tag className="w-3.5 h-3.5 text-teal-600" />
          </div>
          <h4 className="text-[11px] font-bold text-teal-600 uppercase tracking-wider">
            Label Pesanan
          </h4>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" className="sr-only peer" checked={!!config.orderLabelEnabled} onChange={handleToggleLabel} />
          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
        </label>
      </div>
      <p className="text-[11px] text-slate-500 mb-4 pb-4 border-b border-slate-100">
        Cetak label stiker per item pesanan untuk ditempel ke produk pelanggan.
      </p>

      {config.orderLabelEnabled && (
        <div className="space-y-5">
          {/* Auto Print Label */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-800">Auto Print Label</p>
              <p className="text-[10px] text-slate-500 leading-snug max-w-[200px]">Cetak label otomatis setelah transaksi berhasil</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" checked={!!config.autoPrintLabelEnabled} onChange={handleToggleAutoLabel} />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
            </label>
          </div>

          {/* Ukuran Kertas */}
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-2">Ukuran Kertas Label</label>
            <div className="flex gap-3">
              {THERMAL_WIDTHS.map((w) => {
                const active = (config.labelPrinterWidth || "58mm") === w;
                return (
                  <button key={w} type="button" onClick={() => handleWidthChange(w)}
                    className={`px-5 py-2.5 rounded-lg ring-1 text-sm font-semibold transition-colors ${active ? "bg-teal-600 text-white ring-teal-600 shadow-sm shadow-teal-600/20" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"}`}>
                    {w}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mapping Kategori Label */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-teal-600" />
                <label className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">
                  Mapping Kategori Label
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSetAllCategories}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-colors ${
                    isAllCategories
                      ? "bg-teal-600 text-white shadow-xs"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  Semua Kategori
                </button>
                <button
                  type="button"
                  onClick={handleSetCustomCategories}
                  className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-colors ${
                    !isAllCategories
                      ? "bg-teal-600 text-white shadow-xs"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  Pilih Kategori
                </button>
              </div>
            </div>
            <p className="text-[10px] text-slate-500 mb-3">
              Pilih kategori produk yang dicetak pada label pesanan (misal: hanya Minuman). Produk di luar kategori ini tidak akan dicetak.
            </p>

            {!isAllCategories && (
              <div className="bg-slate-50 ring-1 ring-slate-200/70 rounded-xl p-3.5 mb-3">
                <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-slate-200/60">
                  <span className="text-[11px] font-bold text-slate-700">
                    Daftar Kategori ({selectedCategoryIds.length} dipilih)
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleSelectAllCategories}
                      className="text-[10px] font-bold text-teal-600 hover:text-teal-700"
                    >
                      Pilih Semua
                    </button>
                    <span className="text-slate-300 text-[10px]">|</span>
                    <button
                      type="button"
                      onClick={handleClearAllCategories}
                      className="text-[10px] font-bold text-rose-500 hover:text-rose-600"
                    >
                      Hapus Semua
                    </button>
                  </div>
                </div>

                {isLoadingCategories ? (
                  <p className="text-center py-2 text-[11px] text-slate-400">Memuat kategori...</p>
                ) : categories.length === 0 ? (
                  <p className="text-center py-2 text-[11px] text-slate-400">Belum ada data kategori.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1">
                    {categories.map((cat) => {
                      const isSelected = selectedCategoryIds.includes(cat.id);
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => handleToggleCategory(cat.id, cat.name)}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            isSelected
                              ? "bg-teal-600 text-white shadow-xs"
                              : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
                          }`}
                        >
                          {isSelected ? (
                            <CheckSquare className="w-3.5 h-3.5" />
                          ) : (
                            <Square className="w-3.5 h-3.5 text-slate-400" />
                          )}
                          <span>{cat.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {isAllCategories && (
              <div className="bg-teal-50/70 ring-1 ring-teal-200/60 rounded-xl p-3 mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold text-teal-800">Semua Kategori Tercetak</span>
                <span className="text-[10px] font-bold bg-teal-600 text-white px-2 py-0.5 rounded-md">Default</span>
              </div>
            )}
          </div>

          {/* Section: Koneksi Printer Thermal */}
          <div className="pt-2 border-t border-slate-100 space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-teal-600">
                Pilihan Sambungan Printer Label
              </label>
              <button
                type="button"
                onClick={handleTestPrint}
                disabled={testPrinting}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-teal-50 ring-1 ring-teal-200 text-teal-700 text-xs font-semibold hover:bg-teal-100 disabled:opacity-50 transition-colors"
              >
                <Check className="w-3.5 h-3.5" />
                {testPrinting ? "Mencetak..." : "Test Cetak Label"}
              </button>
            </div>

            {/* 1. Direct USB (WebUSB) */}
            <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3.5">
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${usbPrinter ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                  {usbPrinter ? <Check className="w-4 h-4" /> : <Usb className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-900">Cetak langsung USB</p>
                  <p className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">
                    Mengirim nota sebagai ESC POS mentah agar hasil Windows tajam seperti APK. Gunakan Chrome dan sambungkan printer USB sebelum mencetak.
                  </p>
                  {usbPrinter && (
                    <p className="text-[11px] font-medium text-emerald-700 mt-1 truncate">
                      Siap memakai {usbPrinter.productName || "Printer USB"}
                    </p>
                  )}
                  {usbMessage && (
                    <p className={`text-[10px] mt-1 ${usbPrinter ? "text-emerald-700" : "text-amber-700"}`}>
                      {usbMessage}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-slate-200/60">
                {isWebUSBSupported() ? (
                  <>
                    <button
                      type="button"
                      onClick={handleConnectUsb}
                      disabled={usbBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700 disabled:opacity-60"
                    >
                      <Usb className="w-3.5 h-3.5" />
                      {usbBusy ? "Menyambungkan..." : usbPrinter ? "Ganti Printer USB" : "Sambungkan Printer USB"}
                    </button>
                    {usbPrinter && (
                      <button
                        type="button"
                        onClick={handleDisconnectUsb}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-slate-600 ring-1 ring-slate-200 text-xs font-semibold hover:bg-slate-100"
                      >
                        <Unplug className="w-3.5 h-3.5" />
                        Lepas
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-[10px] text-amber-700">WebUSB tidak tersedia pada browser ini.</p>
                )}
              </div>
            </div>

            {/* 2. Serial COM */}
            <div className="rounded-xl bg-indigo-50/60 ring-1 ring-indigo-200/70 p-3.5">
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${serialPrinter ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-600"}`}>
                  {serialPrinter ? <Check className="w-4 h-4" /> : <Usb className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-900">Printer Bluetooth melalui COM</p>
                  <p className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">
                    Untuk printer yang muncul sebagai COM4 atau port COM lain di Windows. Data dikirim langsung sebagai ESC POS tanpa preview browser.
                  </p>
                  {serialPrinter && (
                    <p className="text-[11px] font-medium text-emerald-700 mt-1 truncate">
                      Siap memakai {serialPrinter.name || "Printer Thermal Serial"} pada baud rate {serialPrinter.baudRate || 9600}
                    </p>
                  )}
                  {serialMessage && <p className="text-[10px] text-indigo-700 mt-1">{serialMessage}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-indigo-200/60">
                {isWebSerialSupported() ? (
                  <>
                    <button
                      type="button"
                      onClick={handleConnectSerial}
                      disabled={serialBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-60"
                    >
                      <Usb className="w-3.5 h-3.5" />
                      {serialBusy ? "Menyambungkan..." : serialPrinter ? "Ganti Port COM" : "Sambungkan Port COM"}
                    </button>
                    {serialPrinter && (
                      <button
                        type="button"
                        onClick={handleDisconnectSerial}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-slate-600 ring-1 ring-slate-200 text-xs font-semibold hover:bg-slate-100"
                      >
                        <Unplug className="w-3.5 h-3.5" />
                        Lepas
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-[10px] text-amber-700">Web Serial tidak tersedia pada browser ini.</p>
                )}
              </div>
            </div>

            {/* 3. Windows Spooler Helper */}
            <div className="rounded-xl bg-amber-50/70 ring-1 ring-amber-200/80 p-3.5">
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${windowsPrinter ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {windowsPrinter ? <Check className="w-4 h-4" /> : <Printer className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-900">Printer Windows USB Spooler</p>
                  <p className="text-[10px] text-slate-600 mt-0.5 leading-relaxed">
                    Untuk printer USB003 yang memakai driver POS 80 mm. Helper lokal mengirim raw ESC/POS melalui Windows Spooler tanpa mengganti driver.
                  </p>
                  {windowsPrinter && (
                    <p className="text-[11px] font-medium text-emerald-700 mt-1 truncate">
                      Siap memakai {windowsPrinter.name}
                    </p>
                  )}
                  {windowsMessage && <p className="text-[10px] text-amber-800 mt-1">{windowsMessage}</p>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-2 border-t border-amber-200/60">
                <button
                  type="button"
                  onClick={handleFindWindowsPrinters}
                  disabled={windowsBusy}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-60 transition-colors shadow-xs"
                >
                  <Printer className="w-3.5 h-3.5" />
                  {windowsBusy ? "Mencari..." : "Cari Printer Windows"}
                </button>

                <a
                  href={HELPER_DOWNLOAD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white text-amber-900 ring-1 ring-amber-300 text-xs font-semibold hover:bg-amber-100/80 transition-colors shadow-xs"
                >
                  <Download className="w-3.5 h-3.5 text-amber-700" />
                  Download Helper
                </a>

                {windowsPrinter && (
                  <button
                    type="button"
                    onClick={handleDisconnectWindowsPrinter}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-slate-700 ring-1 ring-slate-200 text-xs font-semibold hover:bg-slate-100"
                  >
                    Lepas
                  </button>
                )}
              </div>

              {windowsPrinters.length > 0 && (
                <div className="mt-3 pt-2 border-t border-amber-200/60">
                  <p className="text-[10px] font-bold text-amber-900 mb-1.5">Pilih Printer dari Daftar Windows:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {windowsPrinters.map((pName) => {
                      const active = windowsPrinter?.name === pName;
                      return (
                        <button
                          key={pName}
                          type="button"
                          onClick={() => handleSelectWindowsPrinter(pName)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                            active
                              ? "bg-emerald-600 text-white shadow-xs"
                              : "bg-white ring-1 ring-amber-300 text-amber-900 hover:bg-amber-100/80"
                          }`}
                        >
                          {pName} {active && "✓"}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* 4. Bluetooth Android (Capacitor/Cordova) */}
            <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200/70 p-3.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Printer className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-xs font-semibold text-slate-800">Printer Bluetooth (Aplikasi Android)</p>
                    <p className="text-[10px] text-slate-500">{config.labelPrinterName || "Belum dipilih"}</p>
                  </div>
                </div>
                {isBluetoothConnected ? (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-50 ring-1 ring-emerald-200/60 rounded-md text-[10px] font-bold text-emerald-700">
                    <Wifi className="w-3 h-3" /> Terhubung
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-slate-100 ring-1 ring-slate-200/60 rounded-md text-[10px] font-bold text-slate-500">
                    <WifiOff className="w-3 h-3" /> Belum dipilih
                  </span>
                )}
              </div>

              {showDeviceList && availableDevices.length > 0 && (
                <div className="bg-white ring-1 ring-slate-200 rounded-xl overflow-hidden mt-3 mb-2">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider px-3 py-2 border-b border-slate-100">
                    Pilih Printer Bluetooth
                  </p>
                  {availableDevices.map((d) => (
                    <button
                      key={d.address || d.id}
                      type="button"
                      onClick={() => handleSelectDevice(d)}
                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-teal-50 transition-colors border-b border-slate-100 last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        <Printer className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-xs font-semibold text-slate-800">{d.name || "Perangkat Bluetooth"}</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono">{d.address || d.id}</span>
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-2 mt-3 pt-2 border-t border-slate-200/60">
                <button
                  type="button"
                  onClick={handleScanBluetooth}
                  disabled={isScanning}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700 disabled:opacity-60"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isScanning ? "animate-spin" : ""}`} />
                  {isScanning ? "Memindai..." : isBluetoothConnected ? "Ganti Bluetooth" : "Scan Bluetooth"}
                </button>
                {isBluetoothConnected && (
                  <button
                    type="button"
                    onClick={handleDisconnectBluetooth}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-rose-600 ring-1 ring-rose-200 text-xs font-semibold hover:bg-rose-50"
                  >
                    Lepas
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
